import { useState } from "react";
import { color } from "@trackmark/tokens";
import { RatingStrip } from "./RatingStrip.js";
import { Eyebrow, Well } from "./Shell.js";
import styles from "./Specimen.module.css";

/**
 * The identity specimen — a permanent internal reference, not scaffolding.
 *
 * It renders every state of the rating strip and one ranking row at full density, and is the
 * target of the visual regression suite (`pnpm visual`).
 *
 * Deliberately omitted from design/Cross-5a-Chart-Shelf.dc.html:
 *  - the shelf/spine illustration — the user rejected spine view outright (DESIGN-CONFLICTS D1)
 *  - the "spot ink alternates" row — a design exploration, not part of the chosen identity
 *  - the prose captions — the handoff calls those documentation, not UI
 */

const PALETTE = [
  { hex: color.newsprint, name: "newsprint", use: "ground" },
  { hex: color.band, name: "band", use: "headers, hover" },
  { hex: color.rule, name: "rule", use: "hairlines, empty" },
  { hex: color.ink, name: "ink", use: "text, strip fills" },
  { hex: color.grey, name: "grey", use: "secondary" },
  { hex: color.well, name: "well", use: "list surround" },
];

interface Row {
  rank: string;
  repeat?: boolean;
  move: string;
  moveDir?: "up" | "flat";
  isNew?: boolean;
  art: string;
  artFallback?: string;
  title: string;
  strong?: boolean;
  artist: string;
  score: number | null;
  year: string;
  rated: string;
  state?: "cursor" | "unrated";
}

const ROWS: Row[] = [
  {
    rank: "1",
    move: "=11",
    art: "linear-gradient(150deg,#7C7B63 0%,#5A5A48 46%,#2E3026 100%)",
    title: "Between the Bars",
    strong: true,
    artist: "Elliott Smith",
    score: 10,
    year: "1997",
    rated: "14 Mar 24",
  },
  {
    rank: "1",
    repeat: true,
    move: "▲2",
    moveDir: "up",
    art: "linear-gradient(180deg,#E4D9B4 0%,#E4D9B4 52%,#B4463A 52%,#7C3F58 100%)",
    title: "Holland, 1945",
    artist: "Neutral Milk Hotel",
    score: 10,
    year: "1998",
    rated: "02 Sep 24",
  },
  {
    rank: "3",
    move: "NEW",
    isNew: true,
    art: "linear-gradient(120deg,#D8517F 0%,#C43A5E 55%,#3E6B4A 100%)",
    title: "Pink Pony Club",
    strong: true,
    artist: "Chappell Roan",
    score: 9,
    year: "2023",
    rated: "19 Nov 24",
    state: "cursor",
  },
  {
    rank: "4",
    move: "▼1",
    art: "linear-gradient(200deg,#2F5FA8 0%,#4C86C9 60%,#DDE6EF 100%)",
    title: "Mr. Blue Sky",
    artist: "Electric Light Orchestra",
    score: 9,
    year: "1977",
    rated: "07 Jan 23",
  },
  {
    rank: "5",
    move: "▲4",
    moveDir: "up",
    art: "linear-gradient(135deg,#C9B7D6 0%,#8E6FA6 55%,#3A2C46 100%)",
    title: "Enough for You",
    artist: "Olivia Rodrigo",
    score: 8,
    year: "2021",
    rated: "28 Feb 24",
  },
  {
    rank: "—",
    repeat: true,
    move: "—",
    art: "",
    artFallback: "DEFI\nNITELY",
    title: "Champagne Supernova",
    artist: "Oasis",
    score: null,
    year: "1995",
    rated: "—",
    state: "unrated",
  },
];

function RankingRow({ row }: { row: Row }) {
  return (
    <div className={styles.rankRow} data-state={row.state}>
      <div className={styles.rank} data-repeat={row.repeat ? "true" : undefined}>
        {row.rank}
      </div>
      {row.isNew ? (
        <div className={styles.moveNew}>{row.move}</div>
      ) : (
        <div className={styles.move} data-dir={row.moveDir}>
          {row.move}
        </div>
      )}
      <div>
        {row.artFallback ? (
          <div className={styles.artFallback}>
            {row.artFallback.split("\n").map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        ) : (
          <div className={styles.art} style={{ background: row.art }} />
        )}
      </div>
      <div className={styles.title} data-strong={row.strong ? "true" : undefined}>
        {row.title}
      </div>
      <div className={styles.artist}>{row.artist}</div>
      <RatingStrip value={row.score} ground={row.state === "cursor" ? "ink" : "newsprint"} />
      {row.score === null ? <div className={styles.unr}>UNR</div> : <div className={styles.score}>{row.score}</div>}
      <div className={styles.meta}>{row.year}</div>
      <div className={styles.meta}>{row.rated}</div>
    </div>
  );
}

export function Specimen() {
  // The interactive strip proves hover-to-cursor filling without the rate loop existing yet.
  const [hover, setHover] = useState<number | null>(null);
  const [rated, setRated] = useState<number | null>(null);

  return (
    <div className={styles.page}>
      <section className={styles.section}>
        <div className={styles.sectionTitle}>Palette</div>
        <div className={styles.swatches}>
          {PALETTE.map((p) => (
            <div key={p.name} className={styles.swatch} style={{ background: p.hex }} />
          ))}
        </div>
        <div className={styles.swatchLabels}>
          {PALETTE.map((p) => (
            <div key={p.name}>
              {p.hex}
              <b>{p.name}</b>
              {p.use}
            </div>
          ))}
        </div>
        <div className={styles.spots}>
          <div className={styles.spot}>
            <div className={styles.spotBlock} style={{ background: color.vermilion }} />
            <div>
              {color.vermilion} <b>vermilion</b>
              <br />
              live strip fill, cursor band,
              <br />
              focus ring, upward movement
            </div>
          </div>
          <div className={styles.spot}>
            <div className={styles.spotBlock} style={{ background: color.flash }} />
            <div>
              {color.flash} <b>flash</b>
              <br />
              unrated and needs-review only —
              <br />
              the one &ldquo;this needs you&rdquo; ink
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>Type</div>
        <div className={styles.typeRow}>
          <div className={styles.typeLabel}>DISPLAY · ANTON — RANKS &amp; PAGE TITLES</div>
          <div className={styles.display}>Song Rankings</div>
        </div>
        <div className={styles.typeRow}>
          <div className={styles.typeLabel}>SPINE · BARLOW CONDENSED 600/700 — LABELS, TABS, EYEBROWS</div>
          <div className={styles.spine}>Library · Albums · Incomplete</div>
        </div>
        <div className={styles.typeRow}>
          <div className={styles.typeLabel}>BODY · BARLOW 400 / 500</div>
          <div className={styles.bodyType}>
            Non-mover at 1 for eleven weeks. Elliott Smith, Either/Or, Kill Rock Stars, 1997 — eleven tracks, no bonus
            material on the preferred edition.
          </div>
        </div>
        <div className={styles.typeRow}>
          <div className={styles.typeLabel}>DATA · IBM PLEX MONO 400/500 · TABULAR</div>
          <div className={styles.dataType}>{" 9.03  146  1997\n 8.71   61  1998\n10.00    9  1977"}</div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>The rating strip</div>

        <div className={styles.stripRow}>
          <div className={styles.stripLabel}>EMPTY</div>
          <RatingStrip value={null} size="specimen" numerals />
          <div className={styles.chip}>UNRATED</div>
        </div>

        <div className={styles.stripRow}>
          <div className={styles.stripLabel}>LIVE · 7</div>
          <RatingStrip value={null} live={7} cursor={7} size="specimen" numerals />
          <div className={styles.stripScore} style={{ color: color.vermilion }}>
            7
          </div>
        </div>

        <div className={styles.stripRow}>
          <div className={styles.stripLabel}>SET · 10</div>
          <RatingStrip value={10} size="specimen" numerals />
          <div className={styles.stripScore}>10</div>
        </div>

        <div className={styles.stripRow}>
          <div className={styles.stripLabel}>INPUT</div>
          <RatingStrip
            value={rated}
            live={hover}
            size="input"
            numerals
            onRate={setRated}
            onHover={setHover}
            label="Rate this song"
          />
          <div className={styles.stripScore} style={{ color: hover ? color.vermilion : rated ? undefined : color.rule }}>
            {hover ?? rated ?? "—"}
          </div>
        </div>

        <div className={styles.inWell}>
          <div className={styles.stripLabel}>IN THE WELL</div>
          <RatingStrip value={8} size="well" ground="well" />
          <div className={styles.wellScore}>8</div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>Applied · song rankings at full density</div>
        <Well>
          <div className={styles.rankHead}>
            <div>RANK</div>
            <div>MOVE</div>
            <div />
            <div>TITLE</div>
            <div>ARTIST</div>
            <div>RATING</div>
            <div style={{ textAlign: "right" }}>SCR</div>
            <div style={{ textAlign: "right" }}>YEAR</div>
            <div style={{ textAlign: "right" }}>RATED</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {ROWS.map((row) => (
              <RankingRow key={`${row.rank}-${row.title}`} row={row} />
            ))}
          </div>
        </Well>
        <p className={styles.caption}>
          <b>The head band.</b> A 5px coloured edge on the left of every row, card and panel, carrying exactly one
          meaning at a time: flash means <em>this needs you</em>, vermilion means <em>this is where you are</em>, nothing
          means <em>settled</em>. Rank carries no colour — the numeral is already the loudest thing in the row.
        </p>
      </section>

      <section className={styles.section}>
        <Eyebrow>Phase 1 · design system</Eyebrow>
      </section>
    </div>
  );
}
